/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { has, set } from 'json-pointer';
import moment from 'moment';
import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable';
import { BehaviorSubject, from, merge, of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, concatMap, exhaustMap, filter, map, mergeMap, pluck, switchMap, take, tap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { AppAction, AppState, signOutAction } from '../..';
import config from '../../../config';
import { DairyFARMDB, getDB, storeNames, withRetry_ } from '../../../db/store';
import { IMAGE_STORE, OFFLINE_IMAGE_UPDATE_STORE } from '../../../db/stores/images';
import { PatchSchema } from '../../../db/stores/patches';
import { createEpic, getCommonHeaders } from '../../../utils/epics';
import { computeInitialState, FormUpdatePayload, formUpdateReducer, loadFormActions, persistPatchActions, setFormValue, updateForm } from '../../forms';
import { Evaluation } from './../../../evaluation';
import { clearEvaluation, createEval, createRemoteEval, deleteEvaluation, doPublishActions, evaluationSaved, failedPatchQueued, fetchEvaluation, loadEvaluation, loadEvaluationCompleted, loadEvaluations, loadEvaluationsCompleted, markStuckPatchAsSynced, patchQueued, purgePatchLogs, reportPatchFailureActions, saveCreateEvalRecord, saveEval, setActiveEval, stuckPatchNotFound } from './../actions';
import { ExpressionManager, dataProviderRegistry, NIFormData, parseFieldIdentifier, DFSValueType, FormDefinitionWrapper } from '../../../react/components/form/services';
import { IDBPDatabase, IDBPTransaction } from 'idb';
import { getLocalDefinition, hasLocalDefinition } from '../../../definitions/index';
import { stripTime } from '../../../utils/helpers';

const { EVALS_STORE, EVALS_TO_CREATE_STORE, PENDING_EVAL_PATCHES_STORE, LIVE_EVAL_PATCHES_STORE } = storeNames;

const EVAL_ID_COUNTER = 'EVAL_ID_COUNTER';


export function newEvalId() {
  const currCounter = localStorage.getItem(EVAL_ID_COUNTER) || '1000';
  const newCounter = Number.parseInt(currCounter) + 1;
  localStorage.setItem(EVAL_ID_COUNTER, `${newCounter}`);
  return `DFE:${newCounter}`; // Using a prefix to identify new evals
}

const createEvalEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(createEval)),
    exhaustMap((action) => {
      // Set a temporary ID until the server confirms its creation
      const now = moment();
      const id = newEvalId();

      const formType = action.payload.formId;
      const versions = Object.values(state$.value.forms.items[formType])
        .sort((va, vb) => {
          if (va.version_major < vb.version_major) {
            return -1;
          } else if (va.version_major > vb.version_major) {
            return 1;
          } else {
            return 0;
          }
        });
      let form = versions[versions.length - 1];

      if (hasLocalDefinition(formType)) {
        form = getLocalDefinition(formType);
      }

      const formVersion = form.version_major;

      const newEval: Evaluation = {
        author: state$.value.session.dfdmToken!.sub,
        created: now.toISOString(),
        data: {},
        formType,
        formVersion,
        id: id,
        modified: now.toISOString(),
        published: false,
        firstPublished: undefined,
        dbPublished: false,
      };

      // TODO: Add unit tests to check this behavior
      // Initialize with defaults and set the ID
      const initialBody = computeInitialState(form.definition.data);

      return from(
        Promise.resolve().then(() => {
          if (!has(form.definition, '/hooks/create/importDefaults')) {
            return;
          }

          // Import defaults from the external data providers
          const imports = form.definition.hooks!.create!.importDefaults!;
          return Promise.all(
            // Apply all the data import default values before continuing
            imports.map((config) => {
              return dataProviderRegistry
                .getDataProvider(config.serviceID)
                .getData(config.params)
                .then((data: any) => {
                  // If we don't get data back, don't send a message since it's initialization anyways
                  if (!data) {
                    return;
                  }

                  const mapMgr = new ExpressionManager(new BehaviorSubject<NIFormData>(data));
                  // Issue a patch for every data path mapped if there is a value to set for it
                  for (let [dataPath, mapping] of Object.entries(config.mapping)) {
                    mapMgr.watch(mapping).subscribe((value: DFSValueType) => {
                      // not filtering out null since we may need to clear out a previous value
                      if (value !== undefined && value !== null) {
                        set(initialBody, parseFieldIdentifier(dataPath), value);
                      }
                    });
                  }

                  if ((config as any).params?.wmp) {
                    const requiredActionPlans = data.latestWmp?.actionPlans?.filter(function (plan: any) {
                      return !plan.resolved && plan.due !== null
                    }) ?? [];
                    Object.keys((config as any).params.wmp.mapping).forEach(key => {
                      // todo make sure plan has a due date, otherwise it is not required
                      const matchingPlan = requiredActionPlans.find((plan: any) => {
                        return plan.questionId.toLowerCase() === key.toLowerCase();
                      })
                      if (matchingPlan) {
                        set(initialBody, parseFieldIdentifier((config as any).params.wmp.mapping[key]), true);
                      }
                    })
                  }

                  mapMgr.complete();
                });
            })
          );
        })
      ).pipe(
        switchMap(() =>
          of(
            // persist eval
            saveEval({ evalId: id, eval: newEval }),
            saveCreateEvalRecord({
              id: newEval.id,
              author: newEval.author,
              formId: form.form_id,
              formUuid: form.id,
              newEval: newEval,
            }),
            setFormValue('{}', initialBody, newEval.data, newEval.id),
            setActiveEval(newEval.id, action.payload.history)
          )
        )
      );
    })
  );
});

const queueCreatedEvalsEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(saveCreateEvalRecord)),
    pluck('payload'),
    switchMap((record) => {
      return from(
        withRetry_(function(db: IDBPDatabase<DairyFARMDB>) {
          return db
            .transaction([EVALS_TO_CREATE_STORE], 'readwrite')
            .objectStore(EVALS_TO_CREATE_STORE)
            .add(record)
        })
      ).pipe(map(() => createRemoteEval.request(record)));
    }),
    catchError((e) => of(createRemoteEval.failure(e)))
  );
});

const loadEvalsEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(loadEvaluations)),
    // FIXME: Filter by the current user's permissions
    switchMap((action) => {
      let totalEvalsOfType = 0;
      return from(
        getDB().then((db) => {
          const tx = db.transaction([EVALS_STORE, LIVE_EVAL_PATCHES_STORE, PENDING_EVAL_PATCHES_STORE]);
          return tx
            .objectStore(EVALS_STORE)
            .getAll()
            .then((evals: Evaluation[]) => {
              return evals.filter((evl) => {
                const type = action.payload.type

                // exclude wmp audits from all evaluations lists unless they are specifically requested, since they
                // show up in their own list
                if (!type) {
                  if (evl.formType === 'wmp_audit') {
                    return false;
                  }
                } else if (evl.formType !== type) {
                  return false;
                }

                if (evl.formType) {
                  const minVersion = config.versionMapping[evl.formType]?.min;
                  if (minVersion > evl.formVersion) {
                    return false;
                  }
                }

                const facilityId = action.payload.facilityId;

                if (facilityId && facilityId !== evl.data.facility?.id) {
                  return false;
                }
                if (action.payload.drafts === null) {
                  return true;
                }
                return evl.published !== action.payload.drafts;
              })
            })
            .then((evals) => { 
              // If a search text is provided, filter evaluations by it
              if (action.payload.searchText) {
                const searchText = action.payload.searchText.toLowerCase();
                return evals.filter(evl => evl.data.facility?.name?.toLowerCase().includes(searchText));
              }
              return evals;
            })
            .then((evals) => {  
              // If there are filters then apply those filters
              if (action.payload.filters && (action.payload.filters.evalType || action.payload.filters.dateRange)) {
                let filteredEvals = evals;
                if (action.payload.filters.evalType) {
                  const evalType = action.payload.filters.evalType.toLowerCase();
                  filteredEvals = filteredEvals.filter(evl => evl.formType.toLowerCase().includes(evalType));
                }
                if (action.payload.filters.dateRange && action.payload.filters.dateRange.length == 2) {
                  const startDate = stripTime(action.payload.filters.dateRange[0]);
                  const endDate = stripTime(action.payload.filters.dateRange[1]);
                  filteredEvals = filteredEvals.filter(evl => {
                    const targetDateStr = evl.formType.includes('biosecurity') ? evl.data.overview?.evaluationDate : evl.data.info?.evalDate;
                    if (!targetDateStr) return false;
                    const targetDate = stripTime(new Date(`${targetDateStr}T00:00:00`));
                    const isWithinRange =
                      targetDate.getTime() >= startDate.getTime() &&
                      targetDate.getTime() <= endDate.getTime();
                    return isWithinRange;  
                  })
                }
                return filteredEvals;
              }
              return evals;
            })
            .then((evals) => {
              totalEvalsOfType = evals.length;              
              // assuming sortField is always a date
              const sortField = action.payload.sortField ?? 'modified';
              return evals.sort((a, b) => {
                /** @ts-ignore */
                return b[sortField] ? b[sortField].localeCompare(a[sortField]) : b[sortField] === a[sortField] ? 0 : -1;
              })
            })
            .then((evals) => {
              return evals.slice(action.payload.offset, action.payload.offset + action.payload.limit);
            })
            .then((evals) => Promise.all(evals.map((evl) => loadEvalWithPatches(evl, tx))));
        })
      ).pipe(
        map((evals: Evaluation[]) => {
          const mappedEvals: Record<string, Evaluation> = {};

          evals.forEach(e => {
            mappedEvals[e.id] = e;
          })

          return loadEvaluationsCompleted({ evals: mappedEvals, total: totalEvalsOfType });
        })
      );
    })
  );
});

function loadEvalWithPatches(
  evaluation: string | Evaluation,
  tx?: IDBPTransaction<
    DairyFARMDB,
    (typeof EVALS_STORE | typeof LIVE_EVAL_PATCHES_STORE | typeof PENDING_EVAL_PATCHES_STORE)[]
  >
): Promise<Evaluation> {
  const txPromise = tx
    ? Promise.resolve(tx)
    : getDB().then((db) => db.transaction([EVALS_STORE, PENDING_EVAL_PATCHES_STORE, LIVE_EVAL_PATCHES_STORE]));

  return txPromise.then((tx) => {
    // Don't load evaluation redundantly
    return (typeof evaluation !== 'string'
      ? Promise.resolve(evaluation)
      : tx
          .objectStore(EVALS_STORE)
          .get(evaluation)
          .then((evl) => evl!)
    ).then(function applyLocalPatches(evl) {
      // problem for soem users - evl is undefined here.
      // in those cases it should redirect home
      return tx
        .objectStore(PENDING_EVAL_PATCHES_STORE)
        .get(evl.id)
        .then((pep) => {
          return tx
            .objectStore(LIVE_EVAL_PATCHES_STORE)
            .get(evl.id)
            .then((lep) => {
              let patches: FormUpdatePayload[] = [];
              if (pep) patches = [...pep.patches];
              if (lep) patches = [...patches, ...lep.patches];
              return patches;
            });
        })
        .then((patches) => {
          let evaluation = evl.data;
          patches.forEach((patch) => {
            evaluation = formUpdateReducer(evaluation, updateForm({ evalID: evl.id, payload: patch }));
          });

          return { ...evl, data: evaluation };
        });
    });
  });
}

const loadEvalEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(loadEvaluation)),
    concatMap((action) => from(loadEvalWithPatches(action.payload))),
    concatMap((ev) => {
      const versions = state$.value.forms.items[ev!.formType];
      if (versions && versions[ev.formVersion]) return of(loadEvaluationCompleted(ev));

      const { formType, formVersion } = ev;
      // need to load the form def before we can say we're finished loading eval
      return merge(
        of(
          loadFormActions.request({
            type: formType,
            version: formVersion,
          })
        ),
        action$.pipe(
          filter(isActionOf(loadFormActions.success)),
          pluck('payload'),
          filter((form) => form.form_id === formType && form.version_major === formVersion),
          map(() => loadEvaluationCompleted(ev)),
          take(1)
        )
      );
    })
  );
});

const persistEval = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(saveEval)),
    concatMap((action) => {
      return from(
        withRetry_<IDBValidKey>(function(db: IDBPDatabase<DairyFARMDB>) {
          return db.put(EVALS_STORE, action.payload);
        })
        .then(() => evaluationSaved(action.payload.id))
      );
    })
  );
});

/**
 * When form is updated, create live eval patch with updates.
 * If a live eval patch is already in the store for the eval, just add to it.
 * 
 * Emits patchQueued event for every form update
 */
function saveEvalPatchesEpic(action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) {
  const updateAction$ = action$.pipe(filter(isActionOf(updateForm)));

  return updateAction$.pipe(
    concatMap((action) => {
      // save the patch
      return from(
        withRetry_<IDBValidKey>(function(db: IDBPDatabase<DairyFARMDB>) {
          const transaction = db.transaction([LIVE_EVAL_PATCHES_STORE, EVALS_STORE], 'readwrite');
          const livePatchStore = transaction.objectStore(LIVE_EVAL_PATCHES_STORE);
          return livePatchStore.get(action.meta).then((result) => {
            const uuid = state$.value.evals.items.getIn([action.meta, 'uuid']) || null;

            if (!result) {
              // if there is no live patch for this eval, just create one
              return livePatchStore.add({
                evalId: action.meta,
                patches: [action.payload],
                uuid,
                synced: false
              });
            } else {
              // append patch to the log
              const { evalId, patches } = result;

              const newPatchLog = [...patches];
              const lastPatch = newPatchLog[newPatchLog.length - 1];
              const newPatch = action.payload;

              //if editing the same path as the last patch, just edit the last patch instead of creating a new one
              if (
                lastPatch?.path === newPatch?.path &&
                !/\/-$/.test(newPatch.path) && // repeat addition actions
                lastPatch.op === newPatch.op
              ) {
                // same operation, replace the patch
                newPatchLog.splice(newPatchLog.length - 1, 1, newPatch);
              } else {
                // different patch operation, so append
                newPatchLog.push(newPatch);
              }
              return livePatchStore.put({
                evalId,
                patches: newPatchLog,
                uuid
              });
            }
        }).then(patch => {
          // update the evaluation's 'modified' time in indexedDB
          const evlStore = transaction.objectStore(EVALS_STORE);

          return evlStore
            .get(action.meta)
            .then((evl) => ({
              ...evl!,
              modified: moment().toISOString(),
            }))
            .then((updatedEvl) =>
              evlStore
                .put(updatedEvl)
                .then(() => patch)
            );
        })
      })
      ).pipe(
        map(() => patchQueued(action.payload, action.meta)),
        catchError(e => {
          console.error('Failed to save patch', e);
          return of(persistPatchActions.failure(e));
        })
      );
    })
  );
}

/**
 * Manually report any patches that are failing to sync
 */
const identifyStuckPatchesEpic = createEpic((action$, state$) => {
  return action$.pipe(
    // look through evals for ones that haven't synced
    // for any that haven't synced, see if there are any live patches
    filter(isActionOf(failedPatchQueued)),
    map(action => {
      const evaluation = state$.value.evals.items.get(action.payload) as Evaluation;
      if (evaluation && evaluation.syncStatus === 'FAILED') {
        return evaluation;
      } else {
        return false;
      }
    }),
    filter(evaluation => Boolean(evaluation)),
    switchMap(async evaluation => {
      const stuckPatch = await getDB()
        .then(db => {
          return db.transaction([LIVE_EVAL_PATCHES_STORE], 'readwrite')
            .objectStore(LIVE_EVAL_PATCHES_STORE)
            .get((evaluation as Evaluation)!.id as string) as Promise<PatchSchema>;
        })

      if (stuckPatch) {
        return reportPatchFailureActions.request({
          evaluation: evaluation as Evaluation,
          stuckPatch
        })
      } else {
        return stuckPatchNotFound();
      }
    })
  )
})

const syncStuckPatchesEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(reportPatchFailureActions.request)),
    switchMap((action) => {
      const { evaluation, stuckPatch } = action.payload;

      return ajax
        .put(
          `${config.dfdmHost}/api/evaluations/${evaluation!.formType}/${evaluation!.uuid}/report-sync-failure`,
          JSON.stringify(stuckPatch.patches),
          getCommonHeaders(state$)
        )
        .pipe(
          map((res: AjaxResponse) => {
            return reportPatchFailureActions.success(evaluation.id);
          }),
          catchError((e: AjaxError) => {
            return of(reportPatchFailureActions.failure(evaluation.id));
          })
        );
    })
  )
})

const markStuckPatchesAsSyncedEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(reportPatchFailureActions.success)),
    switchMap(async (action) => {
      const db = await getDB();
      const patch = await db.get(LIVE_EVAL_PATCHES_STORE, action.payload);
      if (patch) {
        const updated = await db.put(LIVE_EVAL_PATCHES_STORE, {...patch, synced: true});

        return markStuckPatchAsSynced(patch.evalId);
      } else {
        return stuckPatchNotFound();
      }
    })
  )
});

/**
 * When an eval that's been published is edited we need to make sure the client treats it as draft again
 */
const flagPostPublishEditsEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(patchQueued)),
    mergeMap((action) => from(getDB().then((db) => db.get(EVALS_STORE, action.meta)))),
    filter((evl) => evl !== undefined && evl.published),
    map((evl) => saveEval({ evalId: evl!.id, eval: { ...evl!, published: false } })) // any other properties to change?
  );
});

/**
 * When patches from server are fetched and they conflict with patches on the client, just remove conflicting patches from client.
 * Every single patch after the conflicting patch is removed. This can lead to significant data loss.
 * 
 * This should be reworked to only remove patches that either directly conflict or depend on a directly conflicting patch (have same qustion number?)
 */
const purgeConflictingPatchesEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(purgePatchLogs.request)),
    pluck('payload'),
    concatMap(({ evalId, fromPatchToPath }) => {
      return from(
        getDB()
          .then((db) => {
            let foundIndex = -1;
            const tx = db.transaction([PENDING_EVAL_PATCHES_STORE, LIVE_EVAL_PATCHES_STORE], 'readwrite');
            const pendingStore = tx.objectStore(PENDING_EVAL_PATCHES_STORE);
            return pendingStore
              .get(evalId)
              .then(function clearPendingLogs(patchLog) {
                if (patchLog) {
                  foundIndex = patchLog.patches.findIndex((patch) => patch.path === fromPatchToPath);
                  if (foundIndex >= 0) {
                    // Clear our all patches from foundIndex on
                    return pendingStore
                      .put({
                        ...patchLog,
                        patches: patchLog.patches.slice(0, foundIndex),
                      })
                      .then(() => {});
                  }
                }
                return Promise.resolve();
              })
              .then(function clearLiveLogs() {
                const liveStore = tx.objectStore(LIVE_EVAL_PATCHES_STORE);
                if (foundIndex >= 0) {
                  // Every patch must go.
                  return liveStore.delete(evalId);
                }
                return liveStore.get(evalId).then((livePatchLog) => {
                  if (livePatchLog) {
                    foundIndex = livePatchLog.patches.findIndex((patch) => patch.path === fromPatchToPath);
                    if (foundIndex >= 0) {
                      return liveStore
                        .put({
                          ...livePatchLog,
                          patches: livePatchLog.patches.slice(0, foundIndex),
                        })
                        .then(() => {});
                    }
                  }
                  return Promise.resolve();
                });
              })
              .catch((e) => purgePatchLogs.failure(e));
          })
          .then(() => purgePatchLogs.success(evalId))
          .catch((e) => purgePatchLogs.failure(e))
      );
    })
  );
});

const saveNewEvaluationsEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf([fetchEvaluation.success, doPublishActions.success])),
    pluck('payload'),
    map((evl) => saveEval({ evalId: evl.id, eval: evl }))
  );
});

// Deletion

const clearEvalEpic = createEpic((action$) => {
  return action$.pipe(
    filter(isActionOf(clearEvaluation.request)),
    concatMap(({ payload }) => {
      // Only delete the 'evals' record since we still want patches to go out
      return from(
        getDB().then((db) => {
          const tx = db.transaction([EVALS_STORE, IMAGE_STORE], 'readwrite');
          return tx
            .objectStore(EVALS_STORE)
            .delete(payload)
            .then(() => clearEvaluation.success(payload))
            .catch((e) => clearEvaluation.failure(e));
        })
      );
    })
  );
});

const deleteEvalEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(deleteEvaluation.request)),
    mergeMap(({ payload }) => {
      const { id, type } = payload;
      return from(
        getDB().then((db) => {
          const tx = db.transaction([EVALS_TO_CREATE_STORE, EVALS_STORE], 'readwrite');
          return tx
            .objectStore(EVALS_TO_CREATE_STORE)
            .get(id)
            .then((e) => {
              if (e) {
                // Delete only needs to occur locally
                return tx
                  .objectStore(EVALS_TO_CREATE_STORE)
                  .delete(id)
                  .then(() => tx.objectStore(EVALS_STORE).delete(id))
                  .then(() => deleteEvaluation.success(id));
              }

              // Delete network resource first
              return tx
                .objectStore(EVALS_STORE)
                .get(id)
                .then(function networkDelete(e) {
                  return fetch(`${config.dfdmHost}/api/evaluations/${type}/${e!.uuid!}`, {
                    method: 'DELETE',
                    headers: { ...getCommonHeaders(state$) },
                  })
                    .then(function handleNetworkResponse(res): AppAction | Promise<AppAction> {
                      switch (res.status) {
                        case 204:
                        case 404:
                          // delete locally
                          const tx = db.transaction(
                            [
                              EVALS_STORE,
                              LIVE_EVAL_PATCHES_STORE,
                              PENDING_EVAL_PATCHES_STORE,
                              OFFLINE_IMAGE_UPDATE_STORE,
                            ],
                            'readwrite'
                          );
                          return (
                            tx
                              .objectStore(EVALS_STORE)
                              .delete(id)
                              .then(() => {
                                tx.objectStore(LIVE_EVAL_PATCHES_STORE).delete(id);
                              })
                              .then(() => {
                                tx.objectStore(PENDING_EVAL_PATCHES_STORE).delete(id);
                              })
                              // TODO: Images do not currently indicate to which eval they belong, so cannot clear them out.
                              .then(() =>
                                tx
                                  .objectStore(OFFLINE_IMAGE_UPDATE_STORE)
                                  .index('evalID')
                                  .openCursor(id)
                                  .then((cursor) => cursor?.delete())
                              )
                              .then(() => deleteEvaluation.success(id))
                          );
                        default:
                          // throw rejection?
                          return deleteEvaluation.failure({
                            message: `Server error (${res.status}): ${res.body}`,
                            name: 'DeleteFailure',
                          });
                      }
                    })
                    .catch((error) => {
                      alert('Delete failed. Please ensure you have an Internet connection and try later.');

                      return deleteEvaluation.failure({
                        name: 'FetchError',
                        message: `Failed to DELETE ${id} from server: ${error}`,
                      });
                    });
                });
            });
        })
      );
    })
  );
});

export default combineEpics(
  createEvalEpic,
  queueCreatedEvalsEpic,
  loadEvalEpic,
  loadEvalsEpic,
  persistEval,
  saveEvalPatchesEpic,
  purgeConflictingPatchesEpic,
  saveNewEvaluationsEpic,
  clearEvalEpic,
  flagPostPublishEditsEpic,
  deleteEvalEpic,
  identifyStuckPatchesEpic,
  syncStuckPatchesEpic,
  markStuckPatchesAsSyncedEpic
);